import { useState, useEffect } from "react"

/**
 * Custom Hook to manage values in localStorage with synchronized state.
 *
 * @template T
 * @param {string} key - The localStorage key where the value will be stored.
 * @param {T} initialValue - The initial value if not found in localStorage.
 * @returns {[T, (value: T | ((prevValue: T) => T)) => void]} - An array with the stored value and a function to update it.
 *
 * @example
 * const [name, setName] = useLocalStorage("name", "John Doe");
 * setName("Jane Doe"); // Updates the state and localStorage
 */
const useLocalStorage = (key, initialValue) => {
  const [storedValue, setStoredValue] = useState(() => {
    try {
      const item = localStorage.getItem(key)
      return item ? JSON.parse(item) : initialValue
    } catch (error) {
      console.error(`Error reading localStorage key "${key}":`, error)
      return initialValue
    }
  })

  // Función para actualizar localStorage y el estado
  const setValue = value => {
    try {
      const valueToStore =
        typeof value === "function" ? value(storedValue) : value
      setStoredValue(valueToStore)
      localStorage.setItem(key, JSON.stringify(valueToStore))

      // Disparar un evento personalizado para notificar el cambio
      window.dispatchEvent(new Event("storageUpdate"))
    } catch (error) {
      console.error(`Error setting localStorage key "${key}":`, error)
    }
  }

  // Escuchar cambios en localStorage (cuando otro componente lo actualiza)
  useEffect(() => {
    const handleStorageChange = event => {
      if (event.key === key || event.type === "storageUpdate") {
        try {
          const item = localStorage.getItem(key)
          setStoredValue(item ? JSON.parse(item) : initialValue)
        } catch (error) {
          console.error(
            `Error updating state from localStorage key "${key}":`,
            error,
          )
        }
      }
    }

    window.addEventListener("storage", handleStorageChange)
    window.addEventListener("storageUpdate", handleStorageChange)

    return () => {
      window.removeEventListener("storage", handleStorageChange)
      window.removeEventListener("storageUpdate", handleStorageChange)
    }
  }, [key, initialValue])

  return [storedValue, setValue]
}

export default useLocalStorage
